/* SIZESOFTX
=========================================================================================
* Descripción: Utilidades para tickets de corte y cierre de caja
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 16/08/2026 | Lic. José Luis Aragón Cervantes  | Estructura de ticket alineada al formato de cierre
*      2        | 19/08/2026 | Lic. José Luis Aragón Cervantes  | Total egresos por suma de magnitudes; leyenda TOTAL CAP REST
 *      3        | 19/08/2026 | Lic. José Luis Aragón Cervantes  | Builder único de ticket de cierre (compartido cierre/reimpresión)
 * ---------------------------------------------------------------------------------------
*/

const numberFrom = (source, keys) => {
    if (!source) return 0;
    for (const key of keys) {
        const raw = source[key];
        if (raw !== undefined && raw !== null && raw !== "") {
            const value = Number(raw);
            if (!Number.isNaN(value)) return value;
        }
    }
    return 0;
};

const sumFromList = (source, listKeys, amountKeys) => {
    if (!source) return 0;
    for (const listKey of listKeys) {
        const list = source[listKey];
        if (Array.isArray(list) && list.length > 0) {
            return list.reduce((sum, item) => sum + numberFrom(item, amountKeys), 0);
        }
    }
    return 0;
};

const hasNumberField = (source, keys) => {
    if (!source) return false;
    return keys.some((key) => source[key] !== undefined && source[key] !== null && source[key] !== "");
};

export const buildCierreCajaTicketRows = (source = {}) => {
    const tarjetaDebito = numberFrom(source, ["totalcreditotarjetadebito", "totaltarjetadebito", "dTotalCreditoTarjetaDebito", "dTotalTarjetaDebito"]);
    const tarjetaCredito = numberFrom(source, ["totalcreditotarjetacredito", "totaltarjetacredito", "dTotalCreditoTarjetaCredito", "dTotalTarjetaCredito"]);
    const vales = numberFrom(source, ["totalvales", "dTotalVales"]);
    const valesPorCambio = numberFrom(source, ["totalvalesporcambio", "dTotalValesPorCambio"]);
    const valesPorCambioGenerados = numberFrom(source, ["totalvalesporcambiogenerados", "dTotalValesPorCambioGenerados"]);
    const transferencia = numberFrom(source, ["totaltransferencia", "dTotalTransferencia"]);
    const apartadosTarjeta = numberFrom(source, ["totalapartadostarjeta", "totalapartadostarjetas", "dTotalApartadosTarjeta"]);
    const creditoClientesTarjeta = numberFrom(source, ["totalcreditoclientestarjeta", "totalcreditoclientestarjetas", "dTotalCreditoClientesTarjeta"]);

    const devoluciones = numberFrom(source, ["totaldevoluciones", "dTotalDevoluciones"]);
    const retirosDirecto = numberFrom(source, ["totalretiros", "totalRetiros", "TotalRetiros", "dTotalRetiros", "dtotalretiros"]);
    const retirosLista = sumFromList(source, ["listaretiros", "listaRetiros", "retiros"], ["montoretiro", "montoRetiro", "MontoRetiro", "dMontoRetiro"]);
    const retiros = Math.abs(retirosLista) > Math.abs(retirosDirecto) ? retirosLista : retirosDirecto;
    const prestamos = numberFrom(source, ["totalprestamos", "dTotalPrestamos"]);
    const abonosPrestamos = numberFrom(source, ["totalabonosprestamos", "dTotalAbonosPrestamos"]);

    const apartados = numberFrom(source, ["totalabonosapartados", "totalapartados", "dTotalAbonosApartados", "dTotalApartados"]);
    const abonosCredito = numberFrom(source, ["totalefectivocredito", "totalabonoscredito", "dTotalEfectivoCredito"]);
    const efectivoRecibido = numberFrom(source, ["totalefectivo", "totaleefectivo", "dTotalEfectivo"]);

    const aperturaCaja = numberFrom(source, ["montoinicialapertura", "totalcapitalinicial", "dTotalCapitalInicial", "montoapertura", "dmontoinicialapertura", "dMontoInicialApertura"]);
    const cierreCaja = numberFrom(source, ["montocierre", "dMontoCierre", "totalconteo", "dTotalConteo", "totalcierre", "dTotalCierre"]);
    const totalTarjetas = tarjetaDebito + tarjetaCredito + vales + apartadosTarjeta + creditoClientesTarjeta;
    const totalBancosCalculado = totalTarjetas + transferencia;
    // Vales por cambio/diferencia: solo representación en ticket, nunca mueven capital ni afectan corte.
    // Abonos a préstamos = ingreso de capital a caja (suma en positivo).
    const totalEfectivoCalculado = apartados + abonosCredito + efectivoRecibido + abonosPrestamos;
    const absDeducciones = Math.abs(devoluciones) + Math.abs(retiros) + Math.abs(prestamos);
    const totalDeduccionesCalculado = absDeducciones > 0 ? absDeducciones * -1 : (numberFrom(source, ["totaldeducciones", "dTotalDeducciones"]) || 0);
    const totalBancos = numberFrom(source, ["totalbancos", "dTotalBancos"]) || totalBancosCalculado;
    const totalEfectivo = numberFrom(source, ["totalefectivocierre", "dTotalEfectivoCierre"]) || totalEfectivoCalculado;
    const totalDeducciones = totalDeduccionesCalculado;
    const totalIngresos = totalBancos + totalEfectivo;
    const totalEgresos = absDeducciones;
    const totalCapRest = totalIngresos - totalEgresos;
    const ventaTotal = totalBancos + totalEfectivo - Math.abs(devoluciones);
    const capitalRestante = totalEfectivo + totalDeducciones;
    const diferencia = (aperturaCaja + capitalRestante) - cierreCaja;

    return {
        rows: [
            { type: "section", label: "INGRESOS INDIRECTOS" },
            { label: "Tarjeta debito", value: tarjetaDebito },
            { label: "Tarjeta credito", value: tarjetaCredito },
            { label: "Vales", value: vales },
            { label: "Apartados tarjeta", value: apartadosTarjeta },
            { label: "Cred. clientes tarj.", value: creditoClientesTarjeta },
            { label: "Total tarjetas", value: totalTarjetas, strong: true },
            { label: "Transf./Depositos", value: transferencia },
            { label: "TOTAL BANCOS", value: totalBancos, strong: true },
            { type: "blank" },
            { type: "section", label: "EGRESOS" },
            { label: "Devoluciones", value: Math.abs(devoluciones) * -1 },
            { label: "Retiros", value: Math.abs(retiros) * -1 },
            { label: "Prestamos", value: Math.abs(prestamos) * -1 },
            { label: "Total egresos", value: totalDeducciones, strong: true },
            { type: "blank" },
            { type: "section", label: "INGRESOS DIRECTOS" },
            { label: "Apartados", value: apartados },
            { label: "Abonos credito", value: abonosCredito },
            { label: "Abonos prestamos", value: abonosPrestamos },
            { label: "Efectivo recibido", value: efectivoRecibido },
            { label: "Vales por cambio gen.", value: valesPorCambioGenerados },
            { label: "Vales cambio", value: Math.abs(valesPorCambio) * -1 },
            { label: "TOTAL EFECTIVO", value: totalEfectivo, strong: true },
            { type: "blank" },
            { type: "box-start" },
            { type: "box-heading", label: "INGRESOS:" },
            { type: "box-row", label: "TOTAL BANCOS", value: totalBancos },
            { type: "box-row", label: "TOTAL EFECTIVO", value: totalEfectivo },
            { type: "box-heading", label: "EGRESOS:" },
            { type: "box-row", label: "DEDUCCIONES", value: totalDeducciones },
            { type: "box-blank" },
            { type: "box-row", label: "TOTAL CAP REST", value: totalCapRest, strong: true },
            { type: "box-blank" },
            { type: "box-row", label: "APERTURA CAJA", value: aperturaCaja },
            { type: "box-row", label: "CIERRE CAJA", value: cierreCaja },
            { type: "box-blank" },
            { type: "box-heading", label: "RESUMEN FINAL:" },
            { type: "box-row", label: "VENTA TOTAL", value: ventaTotal },
            { type: "box-row", label: "DIFERENCIA", value: diferencia, strong: true },
            { type: "box-end" },
        ],
        totals: {
            cierreCaja,
            ventaTotal,
            diferencia,
        },
    };
};

const boxLine = (label, value, formatCurrency) => {
    const labelText = `${label}:`;
    const formatted = formatCurrency(value);
    const total = labelText.length + formatted.length;
    if (total <= 30) {
        const padding = 30 - total;
        return `|${labelText + " ".repeat(padding) + formatted}|\n`;
    }
    return `|${(labelText + formatted).slice(0, 30).padEnd(30)}|\n`;
};

const boxHeadingLine = (label) => {
    if (label.length <= 24) {
        const text = ("      " + label).slice(0, 30).padEnd(30);
        return `|${text}|\n`;
    }
    const pad = Math.max(0, Math.floor((30 - label.length) / 2));
    const text = label.slice(0, 30).padStart(pad + label.length).padEnd(30);
    return `|${text}|\n`;
};

const boxBlankLine = () => `|${" ".repeat(30)}|\n`;

export const buildCierreCajaTicketText = (rows, formatCurrency) => {
    const lines = [];
    rows.forEach((row) => {
        if (row.type === "blank") {
            lines.push("");
            return;
        }
        if (row.type === "section") {
            lines.push(row.label);
            lines.push("-".repeat(32));
            return;
        }
        if (row.type === "box-start" || row.type === "box-end") {
            lines.push("+" + "-".repeat(30) + "+");
            return;
        }
        if (row.type === "box-heading") {
            lines.push(boxHeadingLine(row.label).trimEnd());
            return;
        }
        if (row.type === "box-blank") {
            lines.push(boxBlankLine().trimEnd());
            return;
        }
        if (row.type === "box-row") {
            lines.push(boxLine(row.label, row.value, formatCurrency).trimEnd());
            return;
        }
        const label = `${row.label}:`.padEnd(20);
        lines.push(label + formatCurrency(row.value).padStart(12));
    });
    return lines;
};

export const appendCierreCajaTicketRows = ({ ticketParts, textEncoder, rows, formatCurrency }) => {
    rows.forEach((row) => {
        if (row.type === "blank") {
            ticketParts.push(textEncoder.encode("\n"));
            return;
        }
        if (row.type === "section") {
            ticketParts.push(textEncoder.encode(`${row.label}\n`));
            ticketParts.push(textEncoder.encode("-".repeat(32) + "\n"));
            return;
        }
        if (row.type === "box-start" || row.type === "box-end") {
            ticketParts.push(textEncoder.encode("+" + "-".repeat(30) + "+\n"));
            return;
        }
        if (row.type === "box-heading") {
            ticketParts.push(textEncoder.encode(boxHeadingLine(row.label)));
            return;
        }
        if (row.type === "box-blank") {
            ticketParts.push(textEncoder.encode(boxBlankLine()));
            return;
        }
        if (row.type === "box-row") {
            ticketParts.push(textEncoder.encode(boxLine(row.label, row.value, formatCurrency)));
            return;
        }
        const label = `${row.label}:`.padEnd(20);
        ticketParts.push(textEncoder.encode(label + formatCurrency(row.value).padStart(12) + "\n"));
    });
};

export const appendObservacionesTicket = ({ ticketParts, textEncoder, observaciones }) => {
    ticketParts.push(textEncoder.encode("\nOBSERVACIONES:\n"));
    ticketParts.push(textEncoder.encode("-".repeat(32) + "\n"));
    if (observaciones) {
        ticketParts.push(textEncoder.encode(`${observaciones}\n`));
    }
    for (let i = 0; i < 8; i += 1) {
        ticketParts.push(textEncoder.encode("_".repeat(32) + "\n"));
    }
};

export const buildCierreCajaTicket = ({
    textEncoder,
    data,
    formatCurrency,
    headerDate,
    caja,
    usuario,
    footerLabel = "REIMPRESION CIERRE",
}) => {
    const { rows: ticketResumenRows } = buildCierreCajaTicketRows(data);
    const ticketParts = [textEncoder.encode('\x1B\x40')];

    ticketParts.push(
        textEncoder.encode('\x1B\x61\x01\x1B\x21\x11' + 'CIERRE DE CAJA\n'),
        textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
        textEncoder.encode('Caja: ' + ((caja ?? data.caja) || "") + '\n'),
        textEncoder.encode('Usuario: ' + ((usuario ?? data.usuario) || "") + '\n'),
        textEncoder.encode('Fecha: ' + (headerDate || "") + '\n'),
        textEncoder.encode('-'.repeat(32) + '\n\n'),
    );

    ticketParts.push(
        textEncoder.encode('RESUMEN DE CORTE:\n'),
        textEncoder.encode('-'.repeat(32) + '\n'),
    );

    appendCierreCajaTicketRows({ ticketParts, textEncoder, rows: ticketResumenRows, formatCurrency });
    ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n\n'));

    const monedas = (data.listacantidadmonedasbilletes || []).filter(m => m.cantidad > 0);
    if (monedas.length > 0) {
        ticketParts.push(textEncoder.encode('DESGLOSE DE EFECTIVO:\n'));
        ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n'));

        monedas.forEach(m => {
            const subtotal = m.cantidad * m.valor;
            const label = m.tipo === 'MONEDA' ? 'Mda' : 'Bll';
            ticketParts.push(textEncoder.encode(
                `${label} $${m.valor.toFixed(2)} x ${m.cantidad} = $${subtotal.toFixed(2)}\n`
            ));
        });

        ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n'));
    }

    if (data.observaciones) {
        ticketParts.push(textEncoder.encode('\nObs: ' + data.observaciones + '\n'));
    }

    ticketParts.push(
        textEncoder.encode('\n\n'),
        textEncoder.encode('\x1B\x61\x01' + footerLabel + '\n\n\n'),
        textEncoder.encode('\x1B\x64\x06'),
        textEncoder.encode('\x1D\x56\x00')
    );
    return ticketParts;
};

export const buildTicketBytes = (ticketParts) => {
    const totalLength = ticketParts.reduce((sum, part) => sum + part.length, 0);
    const finalTicket = new Uint8Array(totalLength);
    let offset = 0;
    ticketParts.forEach(part => {
        finalTicket.set(part, offset);
        offset += part.length;
    });
    return finalTicket;
};
